import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ApiError, renewSession, setUnauthorizedHandler } from '@/lib/api';
import { RealtimeProvider } from '@/lib/realtime';
import { ThemeProvider } from '@/lib/theme';
import { ToastProvider } from '@/lib/toast';
import { appUrl, isLandingHost, isLandingPath, isMcpHost } from '@/lib/site';
import { createAppRouter } from '@/router';
import './index.css';

// The app never zooms, like an app (docs/features/mobile.md): Safari ignores `user-scalable=no` for pinching, its
// gesture events don't.
for (const name of ['gesturestart', 'gesturechange']) document.addEventListener(name, (e) => e.preventDefault(), { passive: false });

const queryClient = new QueryClient({
  queryCache: new QueryCache(),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// deskworks.ai is only the landing page and the legal pages, mcp.deskworks.ai has none: any other path is the app's.
const { pathname, search, hash } = window.location;
if ((isLandingHost() && !isLandingPath(pathname)) || isMcpHost()) window.location.replace(appUrl(`${pathname}${search}${hash}`));

const router = createAppRouter(queryClient);

void renewSession();
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void renewSession();
});

setUnauthorizedHandler(() => {
  queryClient.clear();
  const { pathname, href } = router.state.location;
  if (pathname !== '/login') {
    void router.navigate({ to: '/login', search: { redirect: href } });
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <TooltipProvider delayDuration={300}>
            <RealtimeProvider>
              <RouterProvider router={router} />
            </RealtimeProvider>
          </TooltipProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
